import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "../ui/dialog";
import { X, Loader2, File, FileWarning } from "lucide-react";

/**
 * DocumentViewerModal - A full-screen modal for viewing documents and images
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal should close
 * @param {string} fileUrl - The signed URL of the file to display
 * @param {string} fileName - Name of the file being viewed
 * @param {boolean} isLoading - Whether the file URL is being fetched
 */
function DocumentViewerModal({ isOpen, onClose, fileUrl, fileName, isLoading }) {
  const [fileType, setFileType] = useState(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (fileUrl) {
      setHasError(false);
      // Determine file type from URL
      const urlLower = fileUrl.toLowerCase();
      if (urlLower.includes('.pdf') || urlLower.includes('application/pdf')) {
        setFileType('pdf');
      } else if (
        urlLower.includes('.jpg') ||
        urlLower.includes('.jpeg') ||
        urlLower.includes('.png') ||
        urlLower.includes('.webp') ||
        urlLower.includes('image/')
      ) {
        setFileType('image');
      } else {
        // Default to trying to display as image
        setFileType('image');
      }
    }
  }, [fileUrl]);

  const handleDownload = () => {
    if (fileUrl) {
      window.open(fileUrl, "_blank");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] w-[95vw] h-[90vh] p-0 bg-gray-900/95 border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/80 to-transparent">
          <DialogHeader className="flex-1">
            <DialogTitle className="text-white text-sm font-medium truncate pr-4">
              {fileName || "Document Viewer"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              title="Open in new tab"
            >
              <File className="w-4 h-4 text-white" />
            </button>
            <DialogClose asChild>
              <button
                onClick={onClose}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                title="Close"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </DialogClose>
          </div>
        </div>

        {/* Content */}
        <div className="w-full h-full flex items-center justify-center p-4 pt-16 overflow-auto">
          {isLoading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="w-10 h-10 text-white animate-spin" />
              <p className="text-white/70 text-sm">Loading document...</p>
            </div>
          ) : hasError ? (
            <div className="flex flex-col items-center gap-4 text-center px-6">
              <FileWarning className="w-16 h-16 text-amber-400" />
              <p className="text-white text-lg font-medium">Document unavailable or unsupported format</p>
              <p className="text-white/60 text-sm max-w-md">
                This document could not be loaded. It may not have been uploaded yet, may have been removed, or could be an unsupported image format (e.g. HEIC). Try downloading the file instead.
              </p>
            </div>
          ) : fileUrl ? (
            fileType === 'pdf' ? (
              <iframe
                src={fileUrl}
                className="w-full h-full rounded-lg bg-white"
                title={fileName || "Document"}
              />
            ) : (
              <img
                src={fileUrl}
                alt={fileName || "Document"}
                className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg shadow-2xl"
                style={{ maxHeight: 'calc(90vh - 5rem)' }}
                onError={() => {
                  setHasError(true);
                }}
              />
            )
          ) : (
            <div className="flex flex-col items-center gap-4 text-center px-6">
              <FileWarning className="w-16 h-16 text-amber-400" />
              <p className="text-white text-lg font-medium">Document unavailable or unsupported format</p>
              <p className="text-white/60 text-sm max-w-md">
                This document could not be loaded. It may not have been uploaded yet, may have been removed, or could be an unsupported image format (e.g. HEIC). Try downloading the file instead.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

DocumentViewerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fileUrl: PropTypes.string,
  fileName: PropTypes.string,
  isLoading: PropTypes.bool,
};

DocumentViewerModal.defaultProps = {
  isLoading: false,
  fileUrl: null,
  fileName: null,
};

export default DocumentViewerModal;
